// frontend/src/pages/StoreSettings.jsx

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import Tile from '../components/layout/Tile';
import CategorySourceSelect from '../components/settings/CategorySourceSelect';
import ConnectorsPanel from '../components/settings/ConnectorsPanel';
import ExportColumnsDialog from '../components/settings/ExportColumnsDialog';
import {
  InventoryTargetFields, LeadTimeField, OrderCadenceFields,
} from '../components/settings/PlanningDefaultsFields';
import SupplierSourceSelect from '../components/settings/SupplierSourceSelect';
import {
  onlyHiddenDaysEdited, planningDefaultsPayload, planningDefaultsValid,
} from '../components/settings/planningDefaults';
import { useAuth } from '../contexts/AuthContext';
import { useFormatters } from '../hooks/useFormatters';
import { useTabs } from '../tabs/useTabs';
import { sessionService } from '../services/SessionApiService';
import { translateRequestError } from '../queries/api';
import { COMMON_TIMEZONES, languageOptions, regionOptions } from '../utils/localeOptions';
import { PLANNING_HORIZON_DAYS } from '../constants/planning';
import {
  COMPANY_FIELD_MAX, ORG_FIELD_MAX, STORE_FIELD_MAX, overlongFields, lengthLimits,
} from '../constants/orgFieldLimits';
import { DATA, useDataChanged } from '../queries/dataChanged';
import {
  storeSettings as storeSettingsKey,
} from '../queries/keys';
import { extractAllForms } from './storeSettingsForms';
import '../styles/pages.css';
import { showToast } from '../components/common/appToastBus';


const CURRENCY_CODES = [
  'EUR', 'USD', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR', 'PLN',
  'CHF', 'SEK', 'NOK', 'DKK', 'BRL', 'MXN', 'SGD', 'HKD', 'NZD', 'ZAR',
];

/** `EUR — Euro`, the name in the merchant's UI language. */
const currencyOptions = (language) => {
  const names = new Intl.DisplayNames(language, { type: 'currency' });
  return CURRENCY_CODES.map((code) => ({ value: code, label: `${code} \u2014 ${names.of(code)}` }));
};

const UNIT_SYSTEM_OPTIONS = [
  { value: 'metric', labelKey: 'storeSettings.unitSystem_metric' },
  { value: 'imperial', labelKey: 'storeSettings.unitSystem_imperial' },
];

// Keyed by `unit_system`, which the backend constrains to exactly these two
// values (core/models.py UNIT_SYSTEM_CHOICES: metric | imperial, NOT NULL with
// a default). The first entry of each list is that system's default unit — see
// `unitOptionsFor` and `handlePrefsChange`.
const WEIGHT_UNIT_OPTIONS = {
  metric: [
    { value: 'kg', labelKey: 'storeSettings.weightUnit_kg' },
    { value: 'g', labelKey: 'storeSettings.weightUnit_g' },
  ],
  imperial: [
    { value: 'lb', labelKey: 'storeSettings.weightUnit_lb' },
    { value: 'oz', labelKey: 'storeSettings.weightUnit_oz' },
  ],
};

const DIMENSION_UNIT_OPTIONS = {
  metric: [
    { value: 'cm', labelKey: 'storeSettings.dimensionUnit_cm' },
    { value: 'm', labelKey: 'storeSettings.dimensionUnit_m' },
  ],
  imperial: [
    { value: 'in', labelKey: 'storeSettings.dimensionUnit_in' },
    { value: 'ft', labelKey: 'storeSettings.dimensionUnit_ft' },
  ],
};

/**
 * Unit choices for a `unit_system`, strict — no default substitution.
 *
 * Falling back to the metric list for an unrecognized system (a third system
 * added on the model, or the field's contract broken) would show an imperial
 * merchant metric units, which the Preferences Save button then writes straight
 * back over their real setting. Same reasoning as the strict readers in
 * storeSettingsForms.js: a broken contract must surface, not resolve to a
 * plausible-looking wrong answer.
 */
const unitOptionsFor = (map, unitSystem) => {
  const options = map[unitSystem];
  if (!options) {
    throw new Error(`Store settings: unknown unit_system "${unitSystem}"`);
  }
  return options;
};

// Values come from the shared constraint (constants/planning.js, mirroring the
// backend's _VALID_PLANNING_HORIZONS); labels are built per-render from
// `storeSettings.planningHorizonMonths` so they follow the active language.
// Not a module constant: a module-level t() would freeze the English forms at
// import time and never re-render on a language switch. Plural category comes
// from i18next's `count`, which is why Polish gets one/few/many (1 miesiąc,
// 2-4 miesiące, 5-6 miesięcy) without any branching here.
const planningHorizonOptions = (t) => PLANNING_HORIZON_DAYS.map((days) => ({
  value: days,
  label: t('storeSettings.planningHorizonMonths', { count: days / 30 }),
}));

// The `planning.*` half of a 400's per-field error map (SessionApiService
// attaches it as `err.fieldErrors`), re-keyed by bare field name so the
// Planning card can look an error up by the field it renders.
//
// Empty when the failure carried no field map at all — a transport error, or a
// 400 about another section. Those are reported by the card's toast, which
// fires on every failed save regardless.
const PLANNING_ERROR_PREFIX = 'planning.';
const planningFieldErrors = (fieldErrors) => {
  if (!fieldErrors) return {};
  return Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([key]) => key.startsWith(PLANNING_ERROR_PREFIX))
      .map(([key, message]) => [key.slice(PLANNING_ERROR_PREFIX.length), message]),
  );
};

// The editable sections, in one table: how each is read out of the
// `/auth/stores/settings/` payload, how its edits go back, and whether its
// current form holds a value the endpoint would refuse. Everything
// per-section below (edit, dirty, save, discard, in-flight) is generic over
// this map, so adding a section is one entry rather than a new form state, a
// new dirty flag, a new saving flag and three near-identical handlers.
//
// `rejects` lives HERE rather than beside the Save button because a section
// declared without one must not quietly get an enabled Save: a lookup into a
// separate table returns `undefined`, which reads as "nothing wrong". Called
// as a method, a missing entry throws on the first render of that card.
// Each predicate is a pure function of that section's own form.
const SECTIONS = {
  store: {
    toPayload: (f) => ({ store: f }),
    rejects: (f) => overlongFields(f, STORE_FIELD_MAX).length > 0,
  },
  org: {
    toPayload: (f) => ({ organization: f }),
    rejects: (f) => overlongFields(f, ORG_FIELD_MAX).length > 0,
  },
  company: {
    toPayload: (f) => ({ company: f }),
    rejects: (f) => overlongFields(f, COMPANY_FIELD_MAX).length > 0,
  },
  // The whole planning slice: the horizon and the early-alert opt-out as the
  // form holds them, the five store-level defaults through the shared slice
  // (components/settings/planningDefaults.js), which turns the digits the
  // controls held into the numbers the endpoint accepts, leaves out the days
  // of supply under Safety stock, and whose validity rule gates the save.
  planning: {
    toPayload: (f) => ({
      planning: {
        planning_horizon_days: f.planning_horizon_days,
        emergency_alerts_before_order_day: f.emergency_alerts_before_order_day,
        ...planningDefaultsPayload(f),
      },
    }),
    rejects: (f) => !planningDefaultsValid(f),
  },
  po: {
    toPayload: (f) => ({ purchase_orders: f }),
    rejects: () => false,
  },
  exports: {
    toPayload: (f) => ({ exports: f }),
    rejects: () => false,
  },
  // The supplier machinery every ingestion pass runs. A picker over a fixed
  // list, so nothing here can be refused; the endpoint queues one backfill
  // when the value changes (authentication/services/planning_settings.py).
  sourcing: {
    toPayload: (f) => ({ sourcing: f }),
    rejects: () => false,
  },
  // Preferences is one card over two backend homes: store-level locale
  // (language/region_code/timezone) and store-level units. Number/date/time
  // formats are derived from the language+region tag via Intl.* at render
  // time and are not persisted.
  prefs: {
    toPayload: (f) => ({
      locale: {
        language: f.language,
        region_code: f.region_code,
        timezone: f.timezone,
      },
      store_units: {
        unit_system: f.unit_system,
        weight_unit: f.weight_unit,
        dimension_unit: f.dimension_unit,
      },
    }),
    // Every control here is a picker over a fixed option list, so there is no
    // value the merchant can put in this card that the endpoint refuses.
    rejects: () => false,
  },
};

// Page-local state at rest — see `localState` in the component.
const EMPTY_LOCAL = {
  storeId: null, drafts: {}, catPolled: null, catDraft: null, planningErrors: {},
};

// Drop one section's unsaved draft, so it reads off the server payload again.
const withoutSection = (drafts, name) => {
  const next = { ...drafts };
  delete next[name];
  return next;
};

const StoreSettings = () => {
  const translation = useTranslation();
  const { t } = translation;
  const { refreshUser, selectedStore } = useAuth();
  const { formatDate, formatDateTime } = useFormatters();
  const { openTab } = useTabs();
  const queryClient = useQueryClient();
  const changed = useDataChanged();
  const storeId = selectedStore?.id;
  const settingsKey = storeSettingsKey.all(storeId);

  // The payload, and the form sections read out of it. Both live in one
  // cache entry because `extractAllForms` is the payload's contract check: it
  // runs inside the queryFn, so a malformed section fails the whole query (→
  // page-level error) instead of half-seeding the page. The request itself is
  // store-scoped server-side via the X-Store-ID header; the key's storeId
  // segment is what makes the cache entry store-scoped.
  const settingsQuery = useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      try {
        const settings = await sessionService.getStoreSettings();
        return { settings, forms: extractAllForms(settings) };
      } catch (err) {
        // Logged here rather than in the error branch of render, which would
        // re-log on every re-render. Rethrown — the page shows the failure.
        console.error('getStoreSettings failed:', err.message);
        throw err;
      }
    },
  });

  // Everything the merchant has changed but not saved, plus the rebuild poll's
  // latest answer. All of it belongs to ONE store, and the selected store can
  // change under this page without it remounting: another tab switching stores
  // makes AuthContext converge in place (`applyUserContext`, no reload). State
  // stamped with the previous store must never be read against the next store's
  // payload — that would show, and save, one store's values under another's
  // name. `local.storeId` is that stamp; `local` below resolves to a clean
  // slate the moment it stops matching.
  //
  //   drafts    — per-section unsaved forms. `undefined` means "not edited", so
  //               the section reads straight off the payload and `dirty` is
  //               simply "has a draft". Nothing syncs server data into form
  //               state, so there is no re-seeding effect.
  //   catPolled — the category-rebuild poll's last answer, which supersedes
  //               the payload's `catalog` block once one arrives.
  //   catDraft  — the merchant's pending category-source pick.
  //   planningErrors — the `planning.*` messages the last failed Planning save
  //               came back with, keyed by field. Store-stamped like the rest:
  //               a message about store 1's cadence must never be shown
  //               against store 2's.
  const [localState, setLocalState] = useState(EMPTY_LOCAL);
  const local = localState.storeId === storeId ? localState : EMPTY_LOCAL;
  // The patch callback receives the CURRENT store's state, so a caller can't
  // build the next value on top of another store's. Stable per store, which
  // lets the poll effect below depend on it honestly — it changes exactly when
  // the store changes, and that is when the interval should restart.
  const patchLocal = useCallback((patch) => setLocalState(prev => {
    const base = prev.storeId === storeId ? prev : EMPTY_LOCAL;
    return { ...base, storeId, ...patch(base) };
  }), [storeId]);

  const drafts = local.drafts;
  // A planning draft whose only difference is a days edit hidden under Safety
  // stock holds nothing a save would send, so it offers no Save.
  const isDirty = (name) => drafts[name] !== undefined && !(
    name === 'planning'
    && onlyHiddenDaysEdited(drafts.planning, settingsQuery.data.forms.planning)
  );
  const anyDirty = Object.keys(SECTIONS).some(isDirty);
  const putDraft = (name, form) =>
    patchLocal(b => ({ drafts: { ...b.drafts, [name]: form } }));
  // Fields the merchant has just re-typed stop carrying the endpoint's verdict
  // on the value they replaced — see `editSection`.
  const dropPlanningErrors = (fields) => patchLocal(b => ({
    planningErrors: Object.fromEntries(
      Object.entries(b.planningErrors).filter(([field]) => !fields.includes(field)),
    ),
  }));
  const dropDraft = (name) => patchLocal(b => ({
    drafts: withoutSection(b.drafts, name),
    // The card is back on server values, so a 400 it collected is about a
    // value that is no longer on screen. Covers both ways a draft is dropped:
    // Discard, and the successful save in `saveMutation.onSuccess`.
    planningErrors: name === 'planning' ? {} : b.planningErrors,
  }));

  // Category source — an async rebuild, not a section field save, so it sits
  // outside the draft/save machinery: an explicit "rebuild" button queues a
  // pipeline run, then we poll until switch_in_progress clears.
  const [catSaving, setCatSaving] = useState(false);

  // The poll's answer once one has arrived, else the payload's block — read
  // directly, because the backend always sends it and substituting a default
  // would show "taxonomy" to a store that uses product types. Non-null past the
  // loading/error returns below; optional here because the effect must be
  // declared above them.
  const catState = local.catPolled ?? settingsQuery.data?.settings.catalog;
  const switchInProgress = !!catState?.switch_in_progress;
  // A picked source the store is not on yet, with no rebuild running. Unsaved
  // like a section draft, so it shows Rebuild/Discard and arms the guard.
  const catDirty = !switchInProgress
    && local.catDraft !== null && local.catDraft !== catState?.category_source;

  // Poll while a rebuild is in flight, then resync the dropdown to whichever
  // source actually got committed. Keyed on the store as well, so a switch
  // tears the interval down instead of polling for a store nobody is on.
  //
  // A COMPLETED rebuild changes more than this dropdown. It moves the store's
  // category-editing capability (core.category_editing), which every category
  // cell in the app renders from, and it replaces the whole ProductCategory
  // population those cells pick from. Both are cached: the capability rides on
  // `selectedStore` (refreshed only by user-context) and the options under the
  // `productCategories` key. Left alone, Item setup and Data Health would go on
  // offering the OLD store's editor — and every row they wrote would be
  // rejected by ItemSerializer, which is the exact failure the capability
  // exists to prevent. So the transition to "finished" refreshes both.
  useEffect(() => {
    if (!switchInProgress) return undefined;
    const id = setInterval(async () => {
      let data;
      try {
        data = await sessionService.getCategorySource();
      } catch (err) {
        // One failed tick doesn't mean the rebuild failed, so keep polling —
        // but never silently: a poll that can't reach the backend is why a
        // merchant would sit on "rebuilding" forever with nothing to report.
        console.error('getCategorySource poll failed:', err.message);
        return;
      }
      patchLocal(b => ({
        catPolled: data,
        catDraft: data.switch_in_progress ? b.catDraft : null,
      }));
      if (data.switch_in_progress) return;

      // The rebuild rewrote the tree and every item's category.
      changed([DATA.categories, DATA.items]);
      try {
        await refreshUser();
      } catch (err) {
        // A DIFFERENT failure from the one above, and it must not wear that
        // message: the rebuild finished, and what did not happen is the pickup
        // of its result. Every category cell in the app is still rendering the
        // OLD capability, so the merchant is told to reload rather than left
        // to discover it through rejected saves.
        console.error('Category rebuild finished, but the user-context refresh failed:', err?.message || err);
        showToast(t('storeSettings.categorySwitchRefreshFailed'), 'warning');
      }
    }, 4000);
    return () => clearInterval(id);
  }, [switchInProgress, patchLocal, changed, refreshUser, t]);

  const unsavedGuard = useUnsavedChangesGuard(anyDirty || catDirty);

  // Export column visibility — a dialog rather than a section, see the tile.
  const [exportColumnsOpen, setExportColumnsOpen] = useState(false);

  // Logo
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Generic section save — sends only that section's fields. The PATCH
  // response is the same payload shape as the GET, so it gets the same strict
  // read and is written straight into the cache: no refetch to see what we
  // just saved. Only the saved section's draft is dropped — a merchant with
  // unsaved edits in another card keeps them (and keeps the unsaved-changes
  // guard), rather than having them silently replaced by server values.
  const saveMutation = useMutation({
    mutationFn: async ({ name }) => {
      const payload = SECTIONS[name].toPayload(drafts[name]);
      const settings = await sessionService.updateStoreSettings(payload);
      const forms = extractAllForms(settings);
      return { settings, forms };
    },
    // Three outcomes, not two. The AuthContext refresh below is NOT part of
    // the write — it happens after the PATCH has already committed — so its
    // failure must not report "Save failed." for a save that landed: the
    // merchant would re-save a change they already made, or write it off as
    // lost.
    onSuccess: async (result, { name }) => {
      // The save DID commit, so the form re-reads from the response on either
      // branch below. Only the message differs.
      queryClient.setQueryData(settingsKey, result);
      changed([DATA.settings]);
      dropDraft(name);

      // Store name/currency also live in the AuthContext store list, which
      // feeds the store switcher and money formatting elsewhere — and currency
      // is editable here for non-Shopify stores, so a stale label after a
      // currency change is a wrong-number risk. The refresh is therefore not
      // optional and its failure is not swallowed: it is logged and the
      // merchant is told what to do about it.
      try {
        await refreshUser();
        showToast(t('msg.saveSuccess'), 'success');
      } catch (err) {
        console.error(
          'Store settings saved, but the user-context refresh failed:',
          err?.message || err,
        );
        showToast(t('storeSettings.savedRefreshFailed'), 'warning');
      }
    },
    // A 400 from this endpoint is per-FIELD (`_put_settings` collects every
    // bad value rather than stopping at the first), so the Planning card puts
    // each `planning.*` message on the control that produced it. The toast
    // still fires on every failure: the field messages say WHICH values were
    // refused, the toast says the save did not land.
    onError: (err, { name }) => {
      console.error('updateStoreSettings failed:', err.message);
      if (name === 'planning') {
        patchLocal(() => ({ planningErrors: planningFieldErrors(err.fieldErrors) }));
      }
      showToast(t('msg.saveFailed'), 'error');
    },
  });

  // Takes the target source as an argument rather than closing over the
  // derived `catShownValue` (declared below the loading/error returns).
  const handleSwitchCategory = async (categorySource) => {
    setCatSaving(true);
    try {
      const data = await sessionService.switchCategorySource(categorySource);
      patchLocal(() => ({ catPolled: data }));
      // Every outcome api/services/catalog_derivations.py
      // request_category_source_switch returns. queue_unavailable is still in
      // progress: the watchdog re-sends the run. `applied` is a store with no
      // items to revisit, so the switch finished inside the request.
      const messages = {
        queued: t('storeSettings.categorySwitchQueued'),
        queue_unavailable: t('storeSettings.categorySwitchQueued'),
        already_running: t('storeSettings.categoryRebuilding'),
        no_change: t('storeSettings.categorySwitchNoChange'),
        applied: t('msg.saveSuccess'),
      };
      if (!(data.outcome in messages)) {
        throw new Error(`Unknown category switch outcome: ${data.outcome}`);
      }
      // already_running queued nothing: another run holds the items.
      const severity = data.outcome === 'already_running' ? 'warning' : 'success';
      showToast(messages[data.outcome], severity);
    } catch (err) {
      console.error('switchCategorySource failed:', err.message);
      showToast(translateRequestError(err, t, 'storeSettings.categorySwitchFailed'), 'error');
    } finally {
      setCatSaving(false);
    }
  };

  // Logo upload / delete — both change `company.logo_url`, so the
  // settings are read again rather than the URL tracked in local state.
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLogoUploading(true);
    try {
      await sessionService.uploadLogo(file);
      await changed([DATA.settings]);
      showToast(t('storeSettings.logoUploaded'), 'success');
    } catch (err) {
      console.error('uploadLogo failed:', err.message);
      showToast(translateRequestError(err, t, 'storeSettings.logoUploadFailed'), 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    setLogoUploading(true);
    try {
      await sessionService.deleteLogo();
      await changed([DATA.settings]);
      showToast(t('storeSettings.logoRemoved'), 'success');
    } catch (err) {
      console.error('deleteLogo failed:', err.message);
      showToast(translateRequestError(err, t, 'storeSettings.logoRemoveFailed'), 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  if (settingsQuery.isPending) {
    return (
      <div className="page-container">
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Stack>
      </div>
    );
  }

  if (settingsQuery.isError) {
    return (
      <div className="page-container">
        <PageBanner severity="error" messages={[translateRequestError(settingsQuery.error, t, 'common.fetchError')]} />
      </div>
    );
  }

  // Past this line the payload and every form section is non-null by
  // construction — `forms` came out of the queryFn, which validated it.
  // Anything that dereferences them belongs BELOW here, never above.
  // StoreSettings.render.test.jsx holds that ordering.
  const { settings, forms } = settingsQuery.data;

  const store = settings.store;
  const isShopify = store?.store_type === 'shopify';
  const logoUrl = settings.company.logo_url;

  // A section's current values: its unsaved draft, else the saved payload.
  const sectionForm = (name) => drafts[name] ?? forms[name];
  const storeForm = sectionForm('store');
  const orgForm = sectionForm('org');
  const companyForm = sectionForm('company');
  const prefsForm = sectionForm('prefs');
  const planningForm = sectionForm('planning');
  const poForm = sectionForm('po');
  const exportsForm = sectionForm('exports');

  const currentWeightUnits = unitOptionsFor(WEIGHT_UNIT_OPTIONS, prefsForm.unit_system);
  const currentDimensionUnits = unitOptionsFor(DIMENSION_UNIT_OPTIONS, prefsForm.unit_system);

  // "Too long" as a field error the merchant can read and act on, rather than
  // a generic save failure a round trip later; `SECTIONS[name].rejects`
  // withholds that card's Save.
  const orgLengths = lengthLimits(orgForm, ORG_FIELD_MAX, t);
  const companyLengths = lengthLimits(companyForm, COMPANY_FIELD_MAX, t);
  const storeLengths = lengthLimits(storeForm, STORE_FIELD_MAX, t);

  const editSection = (name, patch) => {
    putDraft(name, { ...sectionForm(name), ...patch });
    // A server message describes the value that was SENT. The moment the
    // merchant changes that field it no longer applies, and leaving it up
    // would mark a value the endpoint has never seen as refused.
    if (name === 'planning') dropPlanningErrors(Object.keys(patch));
  };

  const fieldHandler = (name, read) => (field) => (e) =>
    editSection(name, { [field]: read(e) });
  const fromValue = (e) => e.target.value;
  const fromChecked = (e) => e.target.checked;

  const handleStoreChange = fieldHandler('store', fromValue);
  const handleOrgChange = fieldHandler('org', fromValue);
  const handleCompanyChange = fieldHandler('company', fromValue);
  const handlePlanningChange = fieldHandler('planning', fromValue);
  const handlePlanningCheckChange = fieldHandler('planning', fromChecked);
  const handlePOChange = fieldHandler('po', fromChecked);
  const handleExportsChange = fieldHandler('exports', fromChecked);

  const handlePrefsChange = (field) => (e) => {
    const value = e.target.value;
    // Switching unit system re-seeds both units to that system's default (the
    // first option), because the current values belong to the old system's
    // list. Read off the option maps rather than a second metric/imperial
    // ternary — the maps are already the source of truth for what each system
    // offers, and two copies of that fact drift.
    const unitDefaults = field === 'unit_system' ? {
      weight_unit: unitOptionsFor(WEIGHT_UNIT_OPTIONS, value)[0].value,
      dimension_unit: unitOptionsFor(DIMENSION_UNIT_OPTIONS, value)[0].value,
    } : null;
    editSection('prefs', { [field]: value, ...unitDefaults });
  };
  const handleTimezoneChange = (_e, value) => editSection('prefs', { timezone: value || '' });

  const catShownValue = catState.switch_in_progress
    ? (catState.pending_target || catState.category_source)
    : (local.catDraft ?? catState.category_source);

  // Per-card header actions (save/discard buttons). Save is withheld while a
  // field in THAT section holds a value the endpoint would reject — over its
  // column width, or a planning number outside its bounds. The request would come
  // back 400 and the merchant would be told only "Failed to save settings",
  // while the field itself already says which one is wrong. Discard stays
  // live, so the state is never a dead end.
  const makeActions = (name) => isDirty(name) ? (
    <>
      <Button
        variant="contained"
        onClick={() => saveMutation.mutate({ name })}
        disabled={saveMutation.isPending || SECTIONS[name].rejects(sectionForm(name))}
        loading={saveMutation.isPending && saveMutation.variables?.name === name}
      >
        {t('btn.saveChanges')}
      </Button>
      <Button
        variant="outlined"
        onClick={() => dropDraft(name)}
        disabled={saveMutation.isPending}
      >
        {t('btn.discardChanges')}
      </Button>
    </>
  ) : null;

  return (
    <div className="page-container">
      {unsavedGuard}

      <div className="tiles-row">
        {/* LEFT COLUMN — Manexys-specific settings */}
        <div className="user-profile-left-column">

          {/* Company details — this store's buyer block on its PO PDFs */}
          <Tile
            title={t('storeSettings.companyDetails')}
            subtitle={t('storeSettings.companyDetailsHint')}
            editActions={makeActions('company')}
          >
            <Stack spacing={2}>
              <TextField
                label={t('storeSettings.companyName')}
                value={companyForm.name}
                onChange={handleCompanyChange('name')}
                size="small"
                fullWidth
                {...companyLengths.propsFor('name')}
              />
              <TextField
                label={t('storeSettings.addressLine1')}
                value={companyForm.address_line1}
                onChange={handleCompanyChange('address_line1')}
                size="small"
                fullWidth
                {...companyLengths.propsFor('address_line1')}
              />
              <TextField
                label={t('storeSettings.addressLine2')}
                value={companyForm.address_line2}
                onChange={handleCompanyChange('address_line2')}
                size="small"
                fullWidth
                {...companyLengths.propsFor('address_line2')}
              />
              <div className="user-profile-field-row">
                <TextField
                  label={t('storeSettings.city')}
                  value={companyForm.city}
                  onChange={handleCompanyChange('city')}
                  size="small"
                  fullWidth
                  {...companyLengths.propsFor('city')}
                />
                <TextField
                  label={t('storeSettings.postalCode')}
                  value={companyForm.postal_code}
                  onChange={handleCompanyChange('postal_code')}
                  size="small"
                  fullWidth
                  {...companyLengths.propsFor('postal_code')}
                />
              </div>
              <div className="user-profile-field-row">
                <TextField
                  label={t('storeSettings.stateProvince')}
                  value={companyForm.state_province}
                  onChange={handleCompanyChange('state_province')}
                  size="small"
                  fullWidth
                  {...companyLengths.propsFor('state_province')}
                />
              </div>
              <div className="user-profile-field-row">
                <TextField
                  label={t('storeSettings.phone')}
                  value={companyForm.phone}
                  onChange={handleCompanyChange('phone')}
                  size="small"
                  fullWidth
                  {...companyLengths.propsFor('phone')}
                />
                <TextField
                  label={t('storeSettings.companyEmail')}
                  value={companyForm.email}
                  onChange={handleCompanyChange('email')}
                  size="small"
                  fullWidth
                  {...companyLengths.propsFor('email')}
                />
              </div>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={t('storeSettings.companyLogo')}
                  sx={{
                    maxWidth: '100%', maxHeight: 80, objectFit: 'contain',
                    borderRadius: 1, border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-fill)', p: 0.5,
                  }}
                />
              ) : (
                <Box sx={{
                  width: '100%', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 1, border: '1px dashed var(--color-border)', color: 'var(--color-text-secondary)',
                  backgroundColor: 'var(--color-fill)',
                }}>
                  <Typography variant="caption">{t('storeSettings.noLogo')}</Typography>
                </Box>
              )}
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => logoInputRef.current?.click()}
                  loading={logoUploading}
                >
                  {logoUrl ? t('storeSettings.changeLogo') : t('storeSettings.uploadLogo')}
                </Button>
                {logoUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleLogoDelete}
                    disabled={logoUploading}
                  >
                    {t('storeSettings.removeLogo')}
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                {t('storeSettings.logoHint')}
              </Typography>
            </Stack>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
          </Tile>

          {/* Organization — the account every store of the business sits in */}
          <Tile
            title={t('storeSettings.organization')}
            editActions={makeActions('org')}
          >
            <Stack spacing={2}>
              <TextField
                label={t('storeSettings.orgName')}
                value={orgForm.name}
                onChange={handleOrgChange('name')}
                size="small"
                fullWidth
                {...orgLengths.propsFor('name')}
              />
              <TextField
                select
                label={t('storeSettings.notificationsTo')}
                value={orgForm.notifications_to}
                onChange={handleOrgChange('notifications_to')}
                size="small"
                fullWidth
                helperText={t('storeSettings.notificationsToHelp')}
              >
                <MenuItem value="MEMBERS">{t('storeSettings.notificationsTo_members')}</MenuItem>
                <MenuItem value="STORE_CONTACT">{t('storeSettings.notificationsTo_storeContact')}</MenuItem>
              </TextField>
            </Stack>
          </Tile>

          {/* Preferences — regional + formatting */}
          <Tile
            title={isShopify ? t('storeSettings.preferencesManagedByShopify') : t('storeSettings.preferences')}
            editActions={makeActions('prefs')}
          >
            <div className="two-col-row">
              <div className="two-col-group two-col-group--fill">
                <Stack spacing={2}>
                  <TextField
                    select
                    label={t('userProfile.language')}
                    value={prefsForm.language}
                    onChange={handlePrefsChange('language')}
                    size="small"
                    fullWidth
                    disabled={isShopify}
                  >
                    {languageOptions(translation).map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete
                    value={prefsForm.timezone || null}
                    onChange={handleTimezoneChange}
                    options={COMMON_TIMEZONES}
                    freeSolo
                    size="small"
                    fullWidth
                    disabled={isShopify}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('storeSettings.timezone')}
                      />
                    )}
                  />
                  <TextField
                    select
                    label={t('userProfile.region')}
                    value={prefsForm.region_code}
                    onChange={handlePrefsChange('region_code')}
                    size="small"
                    fullWidth
                    disabled={isShopify}
                    helperText={isShopify ? undefined : t('storeSettings.regionHelp')}
                  >
                    {regionOptions(translation, { current: prefsForm.region_code }).map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </div>
              <div className="two-col-divider" />
              <div className="two-col-group two-col-group--fill">
                <Stack spacing={2}>
                  <TextField
                    select
                    label={t('storeSettings.unitSystem')}
                    value={prefsForm.unit_system}
                    onChange={handlePrefsChange('unit_system')}
                    size="small"
                    fullWidth
                    disabled={isShopify}
                  >
                    {UNIT_SYSTEM_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t('storeSettings.weightUnit')}
                    value={prefsForm.weight_unit}
                    onChange={handlePrefsChange('weight_unit')}
                    size="small"
                    fullWidth
                    disabled={isShopify}
                  >
                    {currentWeightUnits.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t('storeSettings.dimensionUnit')}
                    value={prefsForm.dimension_unit}
                    onChange={handlePrefsChange('dimension_unit')}
                    size="small"
                    fullWidth
                    disabled={isShopify}
                  >
                    {currentDimensionUnits.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </div>
            </div>
          </Tile>

          {/* Product categories — Manexys-owned; switching queues an async
              rebuild of every item's category on the pipeline rail. Its
              Rebuild and Discard sit in the header like every section's Save,
              so picking a source never grows the tile. */}
          <Tile
            title={t('storeSettings.categoriesSection')}
            editActions={catDirty ? (
              <>
                <Button variant="contained" onClick={() => handleSwitchCategory(catShownValue)} loading={catSaving}>
                  {t('storeSettings.categoryRebuildButton')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => patchLocal(() => ({ catDraft: null }))}
                  disabled={catSaving}
                >
                  {t('btn.discardChanges')}
                </Button>
              </>
            ) : null}
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                {t('storeSettings.categoriesHint')}
              </Typography>
              {catState.switch_in_progress && (
                <Alert severity="info" icon={<CircularProgress size={16} />}>
                  {t('storeSettings.categoryRebuildingHint')}
                </Alert>
              )}
              <CategorySourceSelect
                value={catShownValue}
                onChange={(e) => patchLocal(() => ({ catDraft: e.target.value }))}
                disabled={catState.switch_in_progress || catSaving}
              />
            </Stack>
          </Tile>

          {/* Supplier setup — which supplier machinery every synchronized
              product runs through. A Store setting like the rest: saved
              through the section machinery, applied by the next ingestion
              pass and one queued backfill. What the previous mode created
              stays (per-vendor never re-points a linked item). */}
          <Tile
            title={t('storeSettings.supplierSourceSection')}
            editActions={makeActions('sourcing')}
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                {t('storeSettings.supplierSourceHint')}
              </Typography>
              <SupplierSourceSelect
                value={sectionForm('sourcing').supplier_source}
                onChange={fieldHandler('sourcing', fromValue)('supplier_source')}
              />
            </Stack>
          </Tile>

          {/* Costs — read-only plus one action, no section form. A cost
              edited in Shopify arrives through the inventory_items webhook
              (shopify_app/views/webhooks.py handle_inventory_item_update);
              the import pulls every cost Shopify holds at once. The stamp is
              the last run that COMPLETED, not the last click
              (authentication/store_payload.py). */}
          {isShopify && (
            <Tile title={t('storeSettings.costsSection')}>
              <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('storeSettings.costsHint')}
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.costsLastImport')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {selectedStore.cost_import_completed_at
                      ? formatDateTime(selectedStore.cost_import_completed_at)
                      : t('storeSettings.costsLastImportNever')}
                  </Typography>
                </Box>
                <Box>
                  <Button variant="outlined" onClick={() => openTab('/cost-import')}>
                    {t('storeSettings.costsImportButton')}
                  </Button>
                </Box>
              </Stack>
            </Tile>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="user-profile-right-column">

          {/* Planning — Manexys-specific, growth-ready */}
          <Tile
            title={t('storeSettings.planning')}
            editActions={makeActions('planning')}
          >
            <Stack spacing={1.5}>
              <TextField
                select
                label={t('storeSettings.planningHorizon')}
                value={planningForm.planning_horizon_days}
                onChange={handlePlanningChange('planning_horizon_days')}
                size="small"
                fullWidth
                helperText={t('storeSettings.planningHorizonHint')}
              >
                {planningHorizonOptions(t).map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              {/* The five store-level defaults that cascade BY REFERENCE
                  (inventory target: Store → Supplier → Item-location; lead
                  time: Store → Supplier → catalog item; order cadence: Store
                  → Supplier). Nothing is copied on save — a supplier or item
                  with no override of its own reads whatever is here. The
                  install setup step renders the same components. */}
              <InventoryTargetFields
                form={planningForm}
                onChange={(patch) => editSection('planning', patch)}
                errors={local.planningErrors}
              />
              <LeadTimeField
                form={planningForm}
                onChange={(patch) => editSection('planning', patch)}
                errors={local.planningErrors}
              />
              <OrderCadenceFields
                form={planningForm}
                onChange={(patch) => editSection('planning', patch)}
                errors={local.planningErrors}
              />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={planningForm.emergency_alerts_before_order_day}
                      onChange={handlePlanningCheckChange('emergency_alerts_before_order_day')}
                      size="small"
                    />
                  }
                  label={t('storeSettings.emergencyAlertsEarly')}
                />
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('storeSettings.emergencyAlertsEarlyHint')}
                </Typography>
              </Box>
            </Stack>
          </Tile>

          {/* Purchase Orders — column visibility for generated PDFs */}
          <Tile
            title={t('storeSettings.purchaseOrderSection')}
            editActions={makeActions('po')}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('storeSettings.poColumnsHint')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={poForm.show_line_number}
                    onChange={handlePOChange('show_line_number')}
                    size="small"
                  />
                }
                label={t('storeSettings.poShowLineNumber')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={poForm.show_supplier_code}
                    onChange={handlePOChange('show_supplier_code')}
                    size="small"
                  />
                }
                label={t('storeSettings.poShowSupplierCode')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={poForm.show_sku}
                    onChange={handlePOChange('show_sku')}
                    size="small"
                  />
                }
                label={t('storeSettings.poShowSku')}
              />
              {/* Drafts print (watermarked DRAFT) like any PO; this decides
                  whether their prices go with them — on the PDF, on the
                  draft's page and on its lines in the PO list, which offer
                  the same switch. Off by default — a merchant sends a draft
                  out to GET prices. The items file has its own switch, in
                  the Exports card. */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={poForm.show_prices_on_drafts}
                    onChange={handlePOChange('show_prices_on_drafts')}
                    size="small"
                  />
                }
                label={t('storeSettings.poShowPricesOnDrafts')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={poForm.show_tax}
                    onChange={handlePOChange('show_tax')}
                    size="small"
                  />
                }
                label={t('storeSettings.poShowTax')}
              />
            </FormGroup>
          </Tile>

          {/* Exports — spreadsheet column visibility, one screen for all of
              them. A button rather than the PO tile's inline checkboxes: this
              covers ten exports and 200-odd columns, which is a dialog's worth
              of screen, not a card's. The dialog PATCHes its own endpoint;
              the card's own checkbox below is the `exports` section of the
              settings payload, saved by the card's Save. */}
          <Tile
            title={t('storeSettings.exportSection')}
            editActions={makeActions('exports')}
          >
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography variant="caption" color="text.secondary">
                {t('storeSettings.exportColumns.hint')}
              </Typography>
              <Button variant="outlined" onClick={() => setExportColumnsOpen(true)}>
                {t('storeSettings.exportColumns.button')}
              </Button>
              {/* The draft PO items file's prices — the CSV/XLSX counterpart
                  of the Purchase orders card's draft-PDF switch. */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportsForm.show_po_prices_on_drafts}
                    onChange={handleExportsChange('show_po_prices_on_drafts')}
                    size="small"
                  />
                }
                label={t('storeSettings.exportPoPricesOnDrafts')}
              />
            </Stack>
          </Tile>

          {/* Store — background context from Shopify / editable for manual */}
          <Tile
            title={t('storeSettings.store')}
            subtitle={store?.domain || undefined}
            editActions={!isShopify ? makeActions('store') : null}
          >
            {isShopify ? (
              <>
                {/* The connection warning is AppLayout's StoreConnectionBanner,
                    on every page; the coloured status pip below is the
                    at-a-glance signal inside the Store card. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.storeName')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 'var(--font-weight-normal)' }}>
                    {store?.name || '\u2014'}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.currency')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 'var(--font-weight-normal)' }}>
                    {store?.currency || '\u2014'}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.contactEmail')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {store?.contact_email || '\u2014'}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.storeType')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    Shopify
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.lastSync')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {store?.last_sync ? formatDateTime(store.last_sync) : '\u2014'}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    {t('storeSettings.connectedSince')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {store?.created_at ? formatDate(store.created_at) : '\u2014'}
                  </Typography>
                </Box>
              </>
            ) : (
              <Stack spacing={2}>
                <TextField
                  label={t('storeSettings.storeName')}
                  value={storeForm.name}
                  onChange={handleStoreChange('name')}
                  size="small"
                  fullWidth
                  {...storeLengths.propsFor('name')}
                />
                <TextField
                  select
                  label={t('storeSettings.currency')}
                  value={storeForm.currency}
                  onChange={handleStoreChange('currency')}
                  size="small"
                  fullWidth
                >
                  {currencyOptions(translation.i18n?.resolvedLanguage).map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t('storeSettings.contactEmail')}
                  value={storeForm.contact_email}
                  onChange={handleStoreChange('contact_email')}
                  size="small"
                  fullWidth
                  {...storeLengths.propsFor('contact_email')}
                />
              </Stack>
            )}
          </Tile>
        </div>
      </div>

      {/* Connected systems — sales channels / ERP / WMS connectors. Lives
          in Store Settings (not the business sidebar) since it's a
          configuration surface. */}
      <div style={{ marginTop: 16 }}>
        <ConnectorsPanel />
      </div>

      <ExportColumnsDialog
        open={exportColumnsOpen}
        onClose={() => setExportColumnsOpen(false)}
      />

    </div>
  );
};

export default StoreSettings;
